/**
 * 競技地図の校正ファイル（`map.txt`）の解析。
 *
 * 原典 GoalView が会場ごとに持っている INI 形式のファイルで、
 * 「地図画像のピクセル座標」と「緯度経度」の対応点が書かれている（`GoalViewF2.cpp:528`）。
 *
 * ```ini
 * [mapping]
 * bmp=map_saku.bmp
 * point_x_1=2735
 * point_y_1=1828
 * point_latitude_1=36.249411
 * point_longitude_1=138.432219
 * ...
 * ```
 *
 * 校正点は最大6点。1〜3点目と4〜6点目でそれぞれ独立したアフィン変換を作り、
 * UTM ゾーンをまたぐ会場では `zone_point`（地図上のX座標）を境に切り替える。
 */

import type { AffineParams } from './utm.ts';
import { fromUtm, solveAffine, toUtm, unapplyAffine } from './utm.ts';
import type { UtmPoint } from './types.ts';

/** 校正点1つ */
export interface CalibrationPoint {
  /** 地図画像のピクセル座標 */
  px: number;
  py: number;
  lat: number;
  lon: number;
  utm: UtmPoint;
}

export interface MapCalibration {
  /** 地図画像のファイル名 */
  bmp: string;
  /** 縮尺 [dot/km]。既定 106（マニュアル p.7「1Km＝106ドット」） */
  scale: number;
  /**
   * ゾーン切り替え位置（地図画像のX座標）。
   * 既定 999999 は「ゾーンをまたがない」の意味。
   */
  zonePoint: number;
  /** 磁気偏角 [deg]（西偏がマイナス） */
  variation: number;
  /** 校正点（1〜6点目のうち、有効なもの） */
  points: CalibrationPoint[];
  /** 1〜3点目から作ったアフィン変換 */
  primary: AffineParams | null;
  /** 4〜6点目から作ったアフィン変換（ゾーンをまたぐ会場のみ） */
  secondary: AffineParams | null;
  /** 解釈しなかった行 */
  ignored: string[];
  /**
   * 校正の対象となる画像の画素数。OziExplorer形式（`ozi.ts`）は `IWH` 行で
   * 明示するので入る。GoalViewの `map.txt` は持たないので null。
   *
   * ★ 入っている場合は**必ず実画像と突き合わせること**。校正は特定の画素数の
   *   画像に対して作られており、縮小・再書き出しされた画像に当てると
   *   何のエラーも出ないまま全体がズレて重なる（competition mapの位置合わせは
   *   目視で気付きにくい種類の誤り。docs/08 §74 と同じ轍を踏まないため）。
   */
  imageWidth?: number | null;
  imageHeight?: number | null;
  /** 投影法の記載（Ozi形式のみ。例: "(UTM) Universal Transverse Mercator"） */
  projection?: string;
}

/**
 * 数値を読む。
 * 原典の既定値 `variation=-7,5` のようにカンマが小数点として使われている例があり、
 * C の `atof` はカンマ以降を無視して -7 と読む。同じ挙動に合わせる。
 */
function parseNumber(value: string | undefined, fallback: number): number {
  if (value === undefined) return fallback;
  const m = /^\s*[-+]?\d*\.?\d*/.exec(value);
  const n = m ? Number(m[0]) : NaN;
  return Number.isFinite(n) ? n : fallback;
}

/**
 * `map.txt` を解析する。
 *
 * @param text ファイル全体（Shift-JIS のコメントが含まれるので、
 *             読み込み側で latin1 として渡してもコメント以外は壊れない）
 */
export function parseMapCalibration(text: string): MapCalibration {
  const values = new Map<string, string>();
  const ignored: string[] = [];
  let section = '';

  for (const raw of text.split('\n')) {
    const line = raw.replace(/[\r\n]+$/, '').trim();
    if (line.length === 0) continue;
    if (line.startsWith(';') || line.startsWith('#')) continue;

    const sec = /^\[(.*)\]$/.exec(line);
    if (sec) {
      section = sec[1].trim().toLowerCase();
      continue;
    }
    const eq = line.indexOf('=');
    if (eq < 0) {
      ignored.push(line);
      continue;
    }
    // 原典は [mapping] だけを読む
    if (section !== 'mapping') {
      ignored.push(line);
      continue;
    }
    values.set(line.slice(0, eq).trim().toLowerCase(), line.slice(eq + 1).trim());
  }

  const points: CalibrationPoint[] = [];
  for (let i = 1; i <= 6; i++) {
    const px = values.get(`point_x_${i}`);
    const py = values.get(`point_y_${i}`);
    const lat = values.get(`point_latitude_${i}`);
    const lon = values.get(`point_longitude_${i}`);
    if (px === undefined || py === undefined || lat === undefined || lon === undefined) continue;

    const latitude = parseNumber(lat, NaN);
    const longitude = parseNumber(lon, NaN);
    // 未設定の点は 0,0 で埋められている。原典もそれを有効な点として扱わない
    if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) continue;
    if (latitude === 0 && longitude === 0) continue;

    points.push({
      px: parseNumber(px, 0),
      py: parseNumber(py, 0),
      lat: latitude,
      lon: longitude,
      utm: toUtm(longitude, latitude),
    });
  }

  const toAffine = (subset: CalibrationPoint[]) =>
    subset.length >= 3
      ? solveAffine(subset.map((p) => ({ utm: p.utm, px: p.px, py: p.py })))
      : null;

  return {
    bmp: values.get('bmp') ?? '',
    scale: parseNumber(values.get('scale'), 106),
    zonePoint: parseNumber(values.get('zone_point'), 999999),
    variation: parseNumber(values.get('variation'), -7),
    points,
    primary: toAffine(points.slice(0, 3)),
    secondary: toAffine(points.slice(3, 6)),
    ignored,
  };
}

/**
 * 緯度経度を地図画像のピクセル座標に変換する。
 *
 * ゾーンをまたぐ会場では、まず1〜3点目の変換を試し、結果が `zonePoint` を
 * 超えるか画像の外へ出た場合に4〜6点目の変換へ切り替える（原典と同じ判定）。
 */
export function toMapPixel(
  cal: MapCalibration,
  lon: number,
  lat: number,
): { px: number; py: number } | null {
  if (!cal.primary) return null;
  const utm = toUtm(lon, lat);
  const p = {
    px: cal.primary.a * utm.easting + cal.primary.b * utm.northing + cal.primary.c,
    py: cal.primary.d * utm.easting + cal.primary.e * utm.northing + cal.primary.f,
  };
  if (cal.secondary && cal.zonePoint !== 999999 && (p.px > cal.zonePoint || p.px < 0)) {
    return {
      px: cal.secondary.a * utm.easting + cal.secondary.b * utm.northing + cal.secondary.c,
      py: cal.secondary.d * utm.easting + cal.secondary.e * utm.northing + cal.secondary.f,
    };
  }
  return p;
}

/**
 * 地図画像のピクセル座標を緯度経度に変換する（`toMapPixel` の逆変換）。
 *
 * 競技地図をタイル化する際、元画像の四隅がどの経緯度に当たるかを求めて
 * 生成範囲（タイル座標の範囲）を決めるのに使う。
 *
 * `toMapPixel` はゾーンをまたぐ会場で「変換した結果が `zonePoint` を
 * 超えたら二次側に切り替える」という向き（経緯度→ピクセル）の判定だが、
 * こちらは既にピクセル座標が分かっているので `px` 自体で判定できる。
 */
export function fromMapPixel(
  cal: MapCalibration,
  px: number,
  py: number,
): { lon: number; lat: number } | null {
  const useSecondary = cal.secondary !== null && cal.zonePoint !== 999999 && px > cal.zonePoint;
  const affine = useSecondary ? cal.secondary : cal.primary;
  const zoneSource = useSecondary ? cal.points.slice(3, 6) : cal.points.slice(0, 3);
  if (!affine || zoneSource.length === 0) return null;

  const utm = unapplyAffine(affine, px, py);
  if (!utm) return null;
  return fromUtm({ easting: utm.easting, northing: utm.northing, zone: zoneSource[0].utm.zone });
}
